import { Navigate, Outlet } from 'react-router-dom-v5-compat';

import { checkMasterModeAccess } from 'new-components/OrgSwitcher/helpers/orgAccess';
import { getAccessToken } from 'utils/accessToken';
import { useFeatureFlag } from 'utils/customHooks/useFeatureFlag';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { setWindowLocation } from 'utils/setWindowLocation';

/**
 * @description This component is used to guard routes from being accessed by
 * an org that has not signed our terms of service.
 *
 * @param url - The URL to navigate to if the org has not signed the terms.
 * @returns render the route content or redirect to terms of service page
 */
export const OpTermsGuard = ({
  url,
  isExternal,
}: {
  url: string;
  isExternal?: boolean;
}) => {
  const { orgId } = getOpParams();
  const accessToken = getAccessToken();

  const { data: token, isPending: isPendingToken } = useOpQuery({
    apiEndpointName: 'describeAccessToken',
    parameters: [accessToken],
    select: (data) => data?.json?.data,
  });

  const { data: identity, isPending: isPendingIdentity } = useOpQuery({
    apiEndpointName: 'describeIdentity',
    parameters: [Number(token?.identity?.id)],
    select: (data) => data?.json?.data,
  });

  const { value: dontForceTerms, isPending: isDontForceTermsPending } =
    useFeatureFlag<boolean>('DONT_FORCE_TERMS_SIGNATURE', orgId);

  const { isLoading: isValidateScopesLoading, isValid: hasScope } =
    useValidateScopes(['o{orgId}-admin:w']);

  const isIdentityWithinOrgNamespace = Boolean(
    identity?.namespace?.org?.id === orgId,
  );

  const canSignTerms = hasScope && isIdentityWithinOrgNamespace;
  const hasMasterModeAccess = checkMasterModeAccess({ token });

  const {
    data: hasSignedTermsAgreement,
    isPending: isCheckingForSignedTermsPending,
  } = useOpQuery({
    apiEndpointName: 'describeOrg', // This will contain the status of the latest Platinum terms agreement for this org
    parameters: [orgId],
    /**
     * org.termsStatus is 'A' if the org has signed the latest acceptable Platinum terms agreement.
     * or 'N' if otherwise
     */
    select: (res) => res?.json?.data?.termsStatus === 'A',
    suppressErrorMessage: true,
  });

  const isLoading =
    isPendingToken ||
    isPendingIdentity ||
    isDontForceTermsPending ||
    isValidateScopesLoading ||
    isCheckingForSignedTermsPending;

  /**
   * Early return if the data necessary to determind the org's terms status is
   * still being retrieved.
   */
  if (isLoading) {
    return null;
  }

  /**
   * Render the navigate/redirect component if:
   *
   * - Org has not signed terms
   * - Feature flag has NOT been set for this org to skip terms
   * - Identity does not have mastermode/support access
   * - Identity has permission to agree to terms:
   *   - has the o{orgId}-admin scope
   *   - identity.namespace.org.id is equal to orgId
   *
   * NOTE: This condition feels a bit confusing and was originally implemented
   * to conditionally render the <Outlet /> component. However, it didn't feel
   * like a good idea to have a scenario where <Outlet /> is not rendered and
   * users would be completely locked out in the instance there's some
   * unforeseen permutation of data involved here.
   */
  const shouldRedirect =
    !hasSignedTermsAgreement &&
    !dontForceTerms &&
    !hasMasterModeAccess &&
    canSignTerms;

  if (shouldRedirect) {
    if (isExternal) {
      setWindowLocation(url);
      return null;
    }
    return <Navigate to={url} />;
  }

  // Otherwise, render the route content
  return <Outlet />;
};
